import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Calendar, ClipboardList, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formsApi, type FormResponseSummary, type FormResponseStatus } from '@/lib/api';
import { toast } from '@/components/ui/Toaster';
import { formatDueDate, RESPONSE_STATUS_CLASSES, RESPONSE_STATUS_LABELS } from './form-utils';

export function ResponseStatusPill({ status, className }: { status: FormResponseStatus; className?: string }) {
  return (
    <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap', RESPONSE_STATUS_CLASSES[status], className)}>
      {RESPONSE_STATUS_LABELS[status]}
    </span>
  );
}

export function DueBadge({ dueDate }: { dueDate?: string | null }) {
  if (!dueDate) return null;
  const d = formatDueDate(dueDate);
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs whitespace-nowrap', d.isOverdue ? 'text-error font-medium' : d.isDueSoon ? 'text-warning' : 'text-grey-500')}>
      <Calendar className="w-3 h-3" /> {d.text}
    </span>
  );
}

/** Start (or resume) a response and go fill it. One place, so every list button behaves the same. */
export function useOpenForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [startingId, setStartingId] = useState<string | null>(null);

  const open = async (formId: string, existing?: FormResponseSummary | null) => {
    if (existing && existing.status !== 'pending') {
      navigate(`/forms/${formId}/fill/${existing.id}`);
      return;
    }
    setStartingId(formId);
    try {
      const response = await formsApi.start(formId);
      queryClient.invalidateQueries({ queryKey: ['myForms'] });
      queryClient.invalidateQueries({ queryKey: ['projectForms'] });
      navigate(`/forms/${formId}/fill/${response.id}`);
    } catch (e: any) {
      toast({ type: 'error', title: "Couldn't open the form", message: e?.message });
    } finally {
      setStartingId(null);
    }
  };

  return { open, startingId };
}

interface FormListRowProps {
  formId: string;
  title: string;
  description?: string | null;
  /** Secondary line: project name, requester, etc. */
  meta?: React.ReactNode;
  myResponse?: FormResponseSummary | null;
  dueDate?: string | null;
  /** Open/repeatable: show "Start" even after submissions. */
  repeatable?: boolean;
  mySubmittedCount?: number;
  /** Where a click on the row goes instead of opening the form (staff view). */
  href?: string;
  right?: React.ReactNode;
}

export function FormListRow({ formId, title, description, meta, myResponse, dueDate, repeatable, mySubmittedCount = 0, href, right }: FormListRowProps) {
  const { open, startingId } = useOpenForm();
  const starting = startingId === formId;
  const status = myResponse?.status;

  const action =
    right ??
    (href ? null : (
      <button
        type="button"
        onClick={() => open(formId, myResponse)}
        disabled={starting}
        className="btn-outline btn-sm shrink-0 inline-flex items-center gap-1.5"
      >
        {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {status === 'submitted' ? (repeatable ? 'Submit another' : 'View') : status === 'draft' ? 'Continue' : 'Start'}
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    ));

  const body = (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
        <ClipboardList className="w-4 h-4 text-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-secondary truncate">{title}</p>
          {status && !repeatable && <ResponseStatusPill status={status} />}
          {repeatable && mySubmittedCount > 0 && (
            <span className="text-xs text-grey-500">
              Submitted {mySubmittedCount}×{status === 'draft' ? ' · draft in progress' : ''}
            </span>
          )}
        </div>
        {description && <p className="text-sm text-grey-500 mt-0.5 line-clamp-2">{description}</p>}
        {(meta || dueDate) && (
          <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-grey-500">
            {meta}
            <DueBadge dueDate={dueDate} />
          </div>
        )}
      </div>
      {action && <div className="self-center">{action}</div>}
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block p-4 hover:bg-grey-50 transition-colors">
        {body}
      </Link>
    );
  }
  return <div className="p-4">{body}</div>;
}
